import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../components/Modal';
import { importDeviceYaml } from '../../api/device';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { useCan } from '../../store';
import { alertDialog } from '../../utils/dialog';

interface YamlImportModalProps {
    open: boolean;
    onClose: () => void;
    /** 导入成功后的回调, 让父组件刷新列表. */
    onImported?: () => void;
}

// 由文件内容粗略提取 device_key 等关键字段做预览, 真正的 YAML 解析在后端完成.
// 我们只显示前 N 行 + 启发式抽几个常见 key, 与原型的"解析预览"卡片对齐.
function quickProbe(text: string): Record<string, string> {
    const lines = text.split(/\r?\n/).slice(0, 100);
    const probe: Record<string, string> = {};
    const wanted: Record<string, string> = {
        device_id: 'device_id',
        device_key: 'device_key',
        family: 'device_family / 架构',
        source: '光源',
        band: '波段',
        standard_power_W: '标准功率',
        max_power_W: '标准功率', // 旧键别名, 与 standard_power_W 同义
        min_stable_power_W: '最小稳定功率',
    };
    for (const ln of lines) {
        const m = ln.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*:\s*(.+?)\s*$/);
        if (m && wanted[m[1]]) probe[wanted[m[1]]] = m[2];
    }
    return probe;
}

const YamlImportModal = ({ open, onClose, onImported }: YamlImportModalProps) => {
    const canImport = useCan('device', 'update');
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<Record<string, string>>({});
    const [rawHead, setRawHead] = useState<string>(''); // 文件头若干行
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // 关闭时清状态, 下次打开是干净的
    useEffect(() => {
        if (!open) {
            setFile(null);
            setPreview({});
            setRawHead('');
            setDragOver(false);
        }
    }, [open]);

    const handleFile = async (f: File) => {
        setFile(f);
        try {
            const text = await f.text();
            setRawHead(text.split(/\r?\n/).slice(0, 12).join('\n'));
            setPreview(quickProbe(text));
        } catch {
            setRawHead('(读取文件失败)');
            setPreview({});
        }
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleFile(f);
    };

    const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) handleFile(f);
    };

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const onSubmit = async () => {
        if (!file) {
            setError('请先选择 YAML 文件');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const { data, message } = await importDeviceYaml(file);
            // 普通用户走审批工单: 导入未生效, 待管理员通过后回放执行
            if (alertApprovalIfPending(message)) {
                onClose();
                return;
            }
            const warns = data?.warnings?.length
                ? `\n⚠ 部分字段未归位:\n${data.warnings.map((w) => `· ${w}`).join('\n')}`
                : '';
            alertDialog(`导入成功: ${data?.deviceKey || ''}${warns}`);
            if (onImported) onImported();
            onClose();
        } catch (e) {
            const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(`导入失败: ${msg}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title="导入设备工况 YAML" width={720}>
            <div className="space-y-4">
                <div className="text-xs text-slate-500">
                    上传{' '}
                    <code className="bg-slate-100 px-1 rounded">configs/devices/*.yaml</code>,
                    自动解析为设备 Profile, 按分层合并预览并校验.
                </div>
                {canImport ? (
                    <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
                        ⚠ 如果 <code className="font-mono">device_key</code> 已存在,本次导入会**覆盖**该设备的全部字段
                        (主表 + 模式 + 校准点 + 热校准). 若需先删除现有设备, 请在"设备树/工况" tab 点击"删除当前设备".
                    </div>
                ) : (
                    <div className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded px-3 py-2">
                        ℹ 上传后会生成 <b>变更审批工单</b>, 待管理员通过后才实际导入
                        (届时若 <code className="font-mono">device_key</code> 已存在, 会覆盖该设备的全部字段).
                    </div>
                )}

                {/* 拖拽上传区. 归属架构/设备身份以 YAML 内容为准, 由后端解析, 前端不再提供选择项. */}
                <div
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-6 text-center text-sm cursor-pointer transition-colors ${
                        dragOver
                            ? 'border-brand-500 bg-brand-50 text-brand-700'
                            : 'border-slate-200 text-slate-400 hover:border-slate-300'
                    }`}
                >
                    {file ? (
                        <div className="space-y-1">
                            <div className="text-slate-700 font-mono text-xs">
                                {file.name}{' '}
                                <span className="text-slate-400">
                                    ({(file.size / 1024).toFixed(1)} KB)
                                </span>
                            </div>
                            <div className="text-[11px] text-brand-600">点击或拖拽其它文件替换</div>
                        </div>
                    ) : (
                        <>
                            拖拽 .yaml / .yml 到此 或{' '}
                            <span className="text-brand-600">点击上传</span>
                            <div className="text-[11px] mt-1">例: s1_blue_10w.yaml</div>
                        </>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".yaml,.yml,text/yaml,application/x-yaml"
                        className="hidden"
                        onChange={onPick}
                    />
                </div>

                {/* 解析预览 */}
                {file && (
                    <>
                        <div>
                            <div className="text-sm font-semibold mb-2">
                                解析预览 ·{' '}
                                <span className="font-mono text-xs text-slate-500">
                                    {file.name}
                                </span>
                            </div>
                            <table className="w-full text-sm">
                                <tbody>
                                    {[
                                        ['device_id', preview['device_id'] || preview['device_key'] || '—'],
                                        ['device_family / 架构', preview['device_family / 架构'] || '—'],
                                        ['光源 / 波段', `${preview['光源'] || '—'} / ${preview['波段'] || '—'}`],
                                        ['标准功率', preview['标准功率'] || '—'],
                                        ['最小稳定功率', preview['最小稳定功率'] || '—'],
                                    ].map(([k, v]) => (
                                        <tr key={k}>
                                            <td
                                                className="td text-slate-400"
                                                style={{ width: 160 }}
                                            >
                                                {k}
                                            </td>
                                            <td className="td font-mono text-xs">{v}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {rawHead && (
                            <div>
                                <div className="text-sm font-semibold mb-2">
                                    文件头预览 (前 12 行)
                                </div>
                                <pre className="text-[11px] font-mono bg-slate-50 p-3 rounded overflow-auto max-h-40">
                                    {rawHead}
                                </pre>
                            </div>
                        )}
                    </>
                )}

                <div className="bg-slate-50 rounded p-3 text-xs text-slate-600">
                    上传后由后端解析并分层合并: family_defaults → shared_models →{' '}
                    <b>selected_device</b> → power_calibration → thermal_calibration;
                    校验结果以导入返回为准 (未归位字段会在成功提示中列出).
                </div>

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-ghost"
                        disabled={submitting}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={!file || submitting}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? '导入中…' : '导入并应用到设备 Profile'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default YamlImportModal;
